import type { ReactNode } from "react";
import PhaseSixIcon, { TIconNames } from "../../basic/phaseSixIcon/PhaseSixIcon";
import type { Icon } from "@phosphor-icons/react";
import { PhosphorIcon } from "../../basic/phosphorIcon/PhosphorIcon";

export enum ButtonVariant {
    PRIMARY = "PRIMARY",
    SECONDARY = "SECONDARY",
}

export enum ButtonColorVariant {
    DEFAULT = "PHASE_6",
    DEFAULT_WHITE = "PHASE_6_WHITE",
    PLANET_POP = "PLANET_POP",
    HIGH_CONTRAST = "HIGH_CONTRAST",
    DANGER = "DANGER",
    SECONDARY = "SECONDARY",
    // New corporate colours; replaces DEFAULT once the colour switch covers the whole app
    LUMINA = "LUMINA",
}

export enum IconPosition {
    LEFT = "LEFT",
    RIGHT = "RIGHT",
}

interface AreaButtonProps {
    buttonId?: string;
    "data-testid"?: string;
    variant: ButtonVariant;
    colorVariant?: ButtonColorVariant;
    iconName?: TIconNames | undefined;
    phosphorIcon?: Icon | undefined;
    iconPosition?: IconPosition;
    disabled?: boolean;
    /** Renders the label screen-reader-only; pair with an icon. */
    hideLabel?: boolean;
    role: "link" | "button"; // Specifies the semantic role of the button, e.g. 'a' for links and 'button' for buttons
    buttonType?: string; // Specifies the HTML button type attribute, e.g. 'button', 'submit' or 'reset'
    href?: string;
    openInNewTab?: boolean;
    onClick?: () => void;
    className?: string;
    children: ReactNode;
}

export const AreaButton = (props: AreaButtonProps) => {
    const variant = props.variant || ButtonVariant.PRIMARY;
    const colorVariant = props.colorVariant || ButtonColorVariant.DEFAULT;
    const iconPosition = props.iconPosition || IconPosition.LEFT;

    const baseClasses =
        "rounded-button border text-center px-s py-xs text-m font-semibold flex flex-row items-center gap-xs justify-center transition-bg duration-200 ease-in-out";

    const colorStyles = {
        [ButtonColorVariant.DEFAULT]: {
            [ButtonVariant.PRIMARY]: {
                base: "bg-p6-orange text-white border border-p6-orange",
                hover: "hover:bg-p6-orange-brighter hover:border-p6-orange-brighter",
                disabled: "bg-p6-orange-darker",
            },
            [ButtonVariant.SECONDARY]: {
                base: "bg-transparent text-p6-grey-dark border border-p6-grey-dark",
                hover: "hover:bg-p6-grey-lightest",
                disabled: "",
            },
        },
        [ButtonColorVariant.DEFAULT_WHITE]: {
            [ButtonVariant.PRIMARY]: {
                base: "bg-p6-orange text-white border border-p6-orange",
                hover: "hover:bg-p6-orange-brighter hover:border-p6-orange-brighter",
                disabled: "bg-p6-orange-darker",
            },
            [ButtonVariant.SECONDARY]: {
                base: "bg-white text-p6-grey-dark border border-p6-grey-dark",
                hover: "hover:bg-p6-grey-lightest",
                disabled: "",
            },
        },
        [ButtonColorVariant.PLANET_POP]: {
            [ButtonVariant.PRIMARY]: {
                base: "bg-pp-blue text-almost-black border-3 border-pp-blue focus-visible:!outline-white",
                hover: "hover:bg-pp-blue-brighter hover:border-pp-blue-brighter",
                disabled: "",
            },
            [ButtonVariant.SECONDARY]: {
                base: "bg-transparent text-white border-3 border-pp-blue focus-visible:!outline-white",
                hover: "hover:bg-pp-midnight-blue-brighter hover:border-pp-blue-brighter",
                disabled: "",
            },
        },
        [ButtonColorVariant.HIGH_CONTRAST]: {
            [ButtonVariant.PRIMARY]: {
                base: "bg-p6-orange-darker text-white border border-p6-orange-darker",
                hover: "hover:bg-p6-orange hover:border-p6-orange",
                disabled: "",
            },
            [ButtonVariant.SECONDARY]: {
                base: "bg-white text-p6-orange-darker border border-p6-orange-darker",
                hover: "hover:bg-p6-orange-light-pastel",
                disabled: "",
            },
        },
        [ButtonColorVariant.DANGER]: {
            [ButtonVariant.PRIMARY]: {
                base: "bg-p6-red text-white border border-p6-red",
                hover: "hover:bg-p6-red-brighter hover:border-p6-red-brighter",
                disabled: "",
            },
            [ButtonVariant.SECONDARY]: {
                base: "bg-white text-p6-red border border-p6-red",
                hover: "hover:bg-p6-red-pastel",
                disabled: "",
            },
        },
        [ButtonColorVariant.SECONDARY]: {
            [ButtonVariant.PRIMARY]: {
                base: "bg-forest-green-800 text-white border border-forest-green-800",
                hover: "hover:bg-forest-green-900 hover:border-forest-green-900",
                disabled: "",
            },
            [ButtonVariant.SECONDARY]: {
                base: "bg-white text-forest-green-800 border border-forest-green-800",
                hover: "hover:bg-forest-green-pastel",
                disabled: "",
            },
        },
    };

    const luminaFocusClasses =
        "focus-visible:outline-4 focus-visible:outline-offset-4 focus-visible:outline-forest-green-700";

    // Hover gets darker instead of lighter, and disabled uses its own colours instead of opacity
    const luminaStyles = {
        [ButtonVariant.PRIMARY]: {
            base: `bg-forest-green-800 text-white border border-forest-green-800 ${luminaFocusClasses}`,
            hover: "hover:bg-forest-green-900 hover:border-forest-green-900 active:bg-forest-green-1000 active:border-forest-green-1000",
            disabled: "bg-stone-200 text-stone-500 border border-stone-200",
        },
        [ButtonVariant.SECONDARY]: {
            base: `bg-transparent text-stone-1000 border border-stone-800 ${luminaFocusClasses}`,
            hover: "hover:bg-stone-200 active:bg-stone-400",
            disabled: "bg-transparent text-stone-500 border border-stone-400",
        },
    };

    const getVariantClasses = () => {
        if (colorVariant === ButtonColorVariant.LUMINA) {
            const styles = luminaStyles[variant];

            return props.disabled
                ? `${styles.disabled} cursor-not-allowed`
                : `${styles.base} ${styles.hover} cursor-pointer`;
        }

        const styles = colorStyles[colorVariant]?.[variant];

        let classes = styles.base;
        classes += props.disabled
            ? ` ${styles.disabled} opacity-30 cursor-not-allowed`
            : ` ${styles.hover} cursor-pointer`;

        return classes;
    };

    const Tag = props.role === "link" ? "a" : "button";
    const tagProps: { [key: string]: any } = {
        id: props.buttonId,
        "data-testid": props["data-testid"],
        className: `${baseClasses} ${getVariantClasses()}${props.className ? ` ${props.className}` : ""}`,
    };

    if (props.onClick) {
        tagProps.onClick = (e: React.MouseEvent) => {
            e.stopPropagation();
            props.onClick?.();
        };
    }

    if (Tag === "a") {
        tagProps.href = props.disabled ? undefined : props.href || "#";
        tagProps.target = props.openInNewTab ? "_blank" : "_self";
        tagProps.rel = props.openInNewTab ? "noopener noreferrer" : undefined;
        tagProps["aria-disabled"] = props.disabled;
        tagProps.role = "link";
    } else {
        tagProps.type = props.buttonType ?? "button";
        tagProps.disabled = props.disabled;
    }

    const renderIcon = () => {
        if (props.phosphorIcon) {
            return (
                <PhosphorIcon
                    phosphorIcon={props.phosphorIcon}
                    fontSize="text-l"
                />
            );
        }
        if (props.iconName) {
            return (
                <PhaseSixIcon
                    name={props.iconName}
                    className="text-xl"
                />
            );
        }
        return null;
    };

    return (
        <Tag {...tagProps}>
            {iconPosition === IconPosition.LEFT && renderIcon()}
            {props.hideLabel ? <span className="sr-only">{props.children}</span> : props.children}
            {iconPosition === IconPosition.RIGHT && renderIcon()}
        </Tag>
    );
};
